import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import {
  X, QrCode, Plus, Loader2, Copy, Check, Trash2,
  Clock, ShieldOff, Users as UsersIcon,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDuration = (iso) => {
  if (!iso) return '—';
  const diff = (new Date(iso).getTime() - Date.now()) / 1000;
  if (diff <= 0) return 'expired';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
};

const STATUS_CLS = {
  active: 'bg-green-100 text-green-800 border-green-200',
  expired: 'bg-gray-100 text-gray-700 border-gray-200',
  revoked: 'bg-red-100 text-red-800 border-red-200',
};

const VolunteerTokenManager = ({ open, zone, canWrite, onClose }) => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState('');
  const [hours, setHours] = useState(8);
  const [freshToken, setFreshToken] = useState(null); // {token, expires_at, ...}
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!zone) return;
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/outreach/zones/${zone.id}/tokens`,
        { withCredentials: true });
      setTokens(r.data?.tokens || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load tokens');
    } finally { setLoading(false); }
  }, [zone]);

  useEffect(() => { if (open) { load(); setFreshToken(null); setError(''); } }, [open, load]);

  if (!open || !zone) return null;

  const create = async () => {
    if (!label.trim()) return;
    setCreating(true); setError('');
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/outreach/zones/${zone.id}/tokens`,
        { volunteer_label: label.trim(), hours }, { withCredentials: true });
      setFreshToken(r.data);
      setLabel('');
      setHours(8);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to create token');
    } finally { setCreating(false); }
  };

  const revoke = async (tokenId) => {
    if (!window.confirm('Revoke this volunteer link? Anyone currently using it will be blocked.')) return;
    await axios.post(`${BACKEND_URL}/api/admin/outreach/tokens/${tokenId}/revoke`, {},
      { withCredentials: true });
    await load();
  };

  const url = freshToken
    ? `${window.location.origin}/outreach/quick-log/${freshToken.token}`
    : '';

  const copy = async () => {
    if (!url) return;
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="token-manager-modal">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="px-5 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
          <div>
            <h3 className="font-heading text-base font-bold text-[#0A0A0A] flex items-center gap-2">
              <QrCode className="w-4 h-4 text-[#BA1826]" /> Volunteer QR &amp; Tokens — {zone.name}
            </h3>
            <p className="text-[11px] text-[#4B5563]">
              Each token grants a volunteer team time-limited access to log conversations + surveys in this zone.
              The QR opens a mobile-friendly page; no login required for the volunteer.
            </p>
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#0A0A0A]" data-testid="token-manager-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 p-2 rounded text-xs">{error}</div>}

          {/* Freshly created token surface (shown once) */}
          {freshToken && (
            <div className="bg-[#FFF8F8] border border-[#BA1826]/30 rounded-lg p-4 space-y-3" data-testid="token-fresh-panel">
              <p className="text-[10px] uppercase tracking-wider font-bold text-[#BA1826]">
                Token created — share this QR or URL with the volunteer team now.
              </p>
              <div className="flex flex-col sm:flex-row items-start gap-3">
                <div className="bg-white p-3 rounded border border-[#E5E7EB]" data-testid="token-qr">
                  <QRCodeSVG value={url} size={140} bgColor="#ffffff" fgColor="#0A0A0A" includeMargin={false} />
                </div>
                <div className="flex-1 space-y-2 w-full">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Volunteer link</p>
                    <div className="flex gap-1.5 items-center">
                      <input value={url} readOnly
                        className="flex-1 px-2 py-1.5 border border-[#E5E7EB] rounded text-[11px] font-mono bg-white"
                        data-testid="token-url-input" />
                      <button onClick={copy}
                        className="px-2 py-1.5 border border-[#E5E7EB] rounded text-xs inline-flex items-center gap-1 hover:bg-white"
                        data-testid="token-copy-btn">
                        {copied ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
                        {copied ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                  </div>
                  <p className="text-[11px] text-[#4B5563]">
                    <b>{freshToken.volunteer_label}</b> · expires in ~{freshToken.hours}h
                  </p>
                  <p className="text-[10px] text-[#9CA3AF]">
                    The full token is only shown right now. Save the QR/URL before closing this modal.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Create new */}
          {canWrite && !freshToken && (
            <div className="bg-white border border-[#E5E7EB] rounded p-3 space-y-2" data-testid="token-create-panel">
              <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Generate volunteer token</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <input value={label} onChange={(e) => setLabel(e.target.value)}
                  placeholder="Crew or volunteer label (e.g. Saturday Crew - East NY)"
                  className="flex-1 px-2.5 py-1.5 border border-[#E5E7EB] rounded text-xs focus:border-[#BA1826] focus:outline-none"
                  data-testid="token-label-input" />
                <select value={hours} onChange={(e) => setHours(parseInt(e.target.value))}
                  className="px-2 py-1.5 border border-[#E5E7EB] rounded text-xs"
                  data-testid="token-hours-select">
                  {[2, 4, 8, 12, 24, 48, 72, 168].map((h) => (
                    <option key={h} value={h}>{h < 24 ? `${h}h` : `${h / 24}d`}</option>
                  ))}
                </select>
                <button onClick={create} disabled={creating || !label.trim()}
                  className="text-xs px-3 py-1.5 bg-[#BA1826] text-white rounded hover:bg-[#9F1421] disabled:opacity-50 inline-flex items-center gap-1.5"
                  data-testid="token-create-btn">
                  {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                  Create token
                </button>
              </div>
            </div>
          )}

          {/* Existing tokens */}
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563] mb-2">Tokens for this zone</p>
            {loading ? (
              <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-[#BA1826]" /></div>
            ) : tokens.length === 0 ? (
              <p className="text-xs text-[#9CA3AF] text-center py-4">No volunteer tokens yet. Create one to get started.</p>
            ) : (
              <ul className="space-y-1" data-testid="token-list">
                {tokens.map((t) => (
                  <li key={t.id}
                    className="bg-white border border-[#E5E7EB] rounded p-2 flex items-center gap-3"
                    data-testid={`token-row-${t.id}`}>
                    <UsersIcon className="w-3.5 h-3.5 text-[#BA1826] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-[#0A0A0A] truncate">{t.volunteer_label}</p>
                      <p className="text-[10px] text-[#9CA3AF]">
                        Created {new Date(t.created_at).toLocaleString()} · {t.use_count || 0} log{t.use_count === 1 ? '' : 's'} submitted
                      </p>
                    </div>
                    <span className={`text-[9px] font-bold uppercase tracking-wider border px-1.5 py-0.5 rounded ${STATUS_CLS[t.status] || ''}`}
                      data-testid={`token-status-${t.id}`}>
                      {t.status}
                    </span>
                    <span className="text-[10px] text-[#4B5563] inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {fmtDuration(t.expires_at)}
                    </span>
                    {canWrite && t.status === 'active' && (
                      <button onClick={() => revoke(t.id)} className="text-red-600 hover:text-red-800"
                        title="Revoke" data-testid={`token-revoke-${t.id}`}>
                        <ShieldOff className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="px-5 py-3 border-t border-[#E5E7EB] flex justify-end">
          <button onClick={onClose}
            className="text-xs px-3 py-1.5 text-[#4B5563] hover:bg-[#F8F9FA] rounded">Close</button>
        </div>
      </div>
    </div>
  );
};

export default VolunteerTokenManager;
